import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { Icon } from "./icons/Icon";
import type { IconName } from "./icons/icon-names";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  /** 主要な押しに併記するキー表記（例: `⏎`）。「打鍵で進めることを常に示す」ため（ui-design.md §5.1）。 */
  readonly keyHint?: ReactNode;
  /**
   * 文字の前に添える線アイコン。押しの意味（やり直す・消す等）を形でも示すためのもので、
   * 装飾として読み上げからは隠す（意味は隣の文字が担う）。
   */
  readonly icon?: IconName;
  readonly children: ReactNode;
}

/** 押し（ボタン）。4種: primary（画面に1つだけ）/ secondary / quiet / ghost（ui-design.md §5.1）。 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", keyHint, icon, children, className, type = "button", ...rest },
  ref,
) {
  const classes = ["btn", `btn--${variant}`, className].filter(Boolean).join(" ");
  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {icon !== undefined && <Icon name={icon} size={18} decorative />}
      <span>{children}</span>
      {keyHint !== undefined && <span className="btn__key">{keyHint}</span>}
    </button>
  );
});
